import { RequestDemoFormId } from '@upright/common/activities/contact-forms/request-demo/types';
import { keysOf } from '@upright/common/utils';
import {
  CustomGtmEvent,
  EmailGtmEvent,
} from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { keyBy, mapValues } from 'lodash';
import { ReactNode, SetStateAction, Dispatch, FC } from 'react';

export enum LeadFormId {
  BOOK_DEMO_CSRD = 'book-demo-csrd',
  BOOK_DEMO_INVESTOR = 'book-demo-investor',
  BOOK_DEMO_INVESTOR_AM = 'book-demo-investor-am',
  BOOK_DEMO_INVESTOR_AO = 'book-demo-investor-ao',
  BOOK_DEMO_INVESTOR_PE = 'book-demo-investor-pe',
  BOOK_DEMO_CORPORATE = 'book-demo-corporate',
  BOOK_DISCOVERY_CALL_CSRD = 'book-discovery-call-csrd',
  CONTACT_GENERIC = 'contact-generic',
  BOOK_DEMO_GENERIC = 'book-demo-generic',
  CONTACT_CSRD = 'contact-csrd',
  CONTACT_INVESTOR = 'contact-investor',
  CONTACT_INVESTOR_AM = 'contact-investor-am',
  CONTACT_INVESTOR_AO = 'contact-investor-ao',
  CONTACT_INVESTOR_PE = 'contact-investor-pe',
  CONTACT_CORPORATE = 'contact-corporate',
  WEBINAR_DOWNLOAD_CSRD = 'webinar-download-csrd',
  WEBINAR_DOWNLOAD_INVESTOR = 'webinar-download-investor',
  WEBINAR_DOWNLOAD_SEPT24_RODENSTOCK = 'webinar-download-sept24-rodenstock',
  WEBINAR_REGISTER_INVESTOR = 'webinar-register-investor',
  WEBINAR_REGISTER_CSRD_FOR_INVESTORS = 'webinar-register-csrd-for-investors',
  WEBINAR_REGISTER_FUND_ODAI_LAUNCH = 'webinar-register-fund-odai-launch',
  WEBINAR_REGISTER_CSRD_DMA_FINANCIAL_EFFECTS = 'webinar-register-csrd-dma-financial-effects',
  WEBINAR_REGISTER_CSRD_DMA_UPDATES = 'webinar-register-csrd-dma-updates',
  WEBINAR_REGISTER_CSRD_DMA_50K_PROFILES = 'webinar-register-csrd-dma-50k-profiles',
  WEBINAR_REGISTER_PE_FUND_LAUNCH = 'webinar-register-pe-fund-launch',
  WEBINAR_REGISTER_EU_OMNIBUS = 'webinar-register-eu-omnibus',
  WEBINAR_REGISTER_ESG_IMPACT_DATA_FOR_PRIVATE_MARKETS = 'webinar-register-esg-impact-data-for-private-markets',
  WEBINAR_REGISTER_DMA_AS_STRATEGIC_TOOL = 'webiner-register-dma-as-strategic-tool',
  WEBINAR_REGISTER_FAILING_DMA = 'webinar-register-failing-dma',
  WEBINAR_REGISTER_PLG_DMA_LAUNCH = 'webinar-register-plg-dma-launch',
  DOCUMENTATION_DOWNLOAD_CSRD = 'documentation-download-csrd',
  DOWNLOAD_FUND_IMPACT_REPORT = 'download-fund-impact-report',
  WEBINAR_REGISTER_RETHINKING_SDG = 'webiner-reginster-rethinking-sdg',
  WEBINAR_REGISTER_STOREBRAND_ASSET_MANAGEMENT = 'webinar-register-storebrand-asset-management',
}

export enum CustomerSegment {
  CORPORATE = 'corporate',
  INVESTOR = 'investor',
  INVESTOR_AM = 'investor-am',
  INVESTOR_AO = 'investor-ao',
  INVESTOR_PE = 'investor-pe',
  GENERAL = 'general',
}

export enum LeadFormCategory {
  CONTACT = 'contact',
  BOOK_DEMO = 'book-demo',
  BOOK_DISCOVERY_CALL = 'book-discovery-call',
  WEBINAR_DOWNLOAD = 'webinar-download',
  WEBINAR_REGISTER = 'webinar-register',
  DOWNLOAD_DOCUMENTATION = 'documentation-download',
  OTHER = 'other',
}

export interface LeadFormContactInfo {
  name: string;
  email: string;
  phone: string;
}

export interface LeadForm {
  id: LeadFormId;
  hubspotId: RequestDemoFormId;
  category: LeadFormCategory;
  segment: CustomerSegment;
  title: string;
  textBelowTitle: ReactNode;
  submitText: string;
  submitEvent: EmailGtmEvent;
  openEvent: CustomGtmEvent;
  interactionEvent: CustomGtmEvent;
  contact?: LeadFormContactInfo;
  askConsent: boolean;
  hideMessage: boolean;
  successTitle: string;
  successMessage: string;
  showOrganizationTypeField?: boolean;
  CustomInputComponent?: FC<{ setCustomFieldValue: Dispatch<SetStateAction<string>> }>;
}

interface Defaults {
  category: LeadFormCategory;
  title: string;
  textBelowTitle: ReactNode;
  submitText: string;
  submitEvent: EmailGtmEvent;
  interactionEvent: CustomGtmEvent;
  openEvent: CustomGtmEvent;
  contact?: LeadFormContactInfo;
  askConsent: boolean;
  hideMessage: boolean;
  successTitle: string;
  successMessage: string;
}

export function getDefaultForCategory(category: LeadFormCategory): Defaults {
  switch (category) {
    case LeadFormCategory.CONTACT:
    case LeadFormCategory.OTHER:
      return {
        category: LeadFormCategory.CONTACT,
        title: 'Contact us',
        textBelowTitle:
          'Please leave your contact information here, and we will be in touch during the next business day.',
        askConsent: false,
        submitText: 'Submit',
        submitEvent: CustomGtmEvent.UA_CONTACT_FORM_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_CONTACT_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_CONTACT_CLICK,
        hideMessage: false,
        successTitle: 'Message sent',
        successMessage: `Thanks for reaching out! We'll be in touch soon.`,
      };
    case LeadFormCategory.BOOK_DEMO:
      return {
        category: LeadFormCategory.BOOK_DEMO,
        title: 'Book a demo',
        textBelowTitle:
          'Please leave your contact information here, and we will be in touch during the next business day.',
        askConsent: false,
        submitText: 'Request demo',
        submitEvent: CustomGtmEvent.UA_BOOK_DEMO_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_BOOK_DEMO_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_BOOK_DEMO_CLICK,
        hideMessage: false,
        successTitle: 'Request sent',
        successMessage: `Thanks for reaching out! We'll be in touch soon.`,
      };
    case LeadFormCategory.BOOK_DISCOVERY_CALL:
      return {
        category: LeadFormCategory.BOOK_DISCOVERY_CALL,
        title: 'Book a discovery call',
        textBelowTitle:
          'Please leave your contact information here, and we will be in touch during the next business day.',
        askConsent: false,
        submitText: 'Submit',
        submitEvent: CustomGtmEvent.UA_BOOK_DEMO_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_BOOK_DEMO_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_BOOK_DEMO_CLICK,
        hideMessage: false,
        successTitle: 'Request sent',
        successMessage: `Thanks for reaching out! We'll be in touch soon.`,
      };
    case LeadFormCategory.WEBINAR_DOWNLOAD:
      return {
        category: LeadFormCategory.WEBINAR_DOWNLOAD,
        title: 'Download webinar',
        textBelowTitle: <b>Fill in your details to access the on-demand webinar:</b>,
        askConsent: true,
        submitText: 'Access the webinar',
        submitEvent: CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_WEBINAR_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_WEBINAR_CLICK,
        hideMessage: true,
        successTitle: 'Request sent',
        successMessage: `You will soon receive an email with a link to the webinar.`,
      };
    case LeadFormCategory.DOWNLOAD_DOCUMENTATION:
      return {
        category: LeadFormCategory.DOWNLOAD_DOCUMENTATION,
        title: 'Download documentation',
        textBelowTitle: <b>Fill in your details to download the documentation:</b>,
        askConsent: false,
        submitText: 'Access the documentation',
        submitEvent: CustomGtmEvent.UA_DOWNLOAD_FORM_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_DOWNLOAD_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_DOWNLOAD_FORM_CLICK,
        hideMessage: true,
        successTitle: 'Request sent',
        successMessage: `You will soon receive an email with a link to the documentation.`,
      };
    case LeadFormCategory.WEBINAR_REGISTER:
      return {
        category: LeadFormCategory.WEBINAR_REGISTER,
        title: 'Sign up to the webinar',
        textBelowTitle: <b>Sign up for the webinar on September 24th by filling out the form:</b>,
        askConsent: true,
        submitText: 'Sign up',
        submitEvent: CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT,
        interactionEvent: CustomGtmEvent.UA_WEBINAR_FORM_INTERACTED,
        openEvent: CustomGtmEvent.UA_WEBINAR_CLICK,
        hideMessage: true,
        successTitle: 'Signed up!',
        successMessage: `Thank you for signing up for the webinar! An email has been sent to you with a link to join the webinar. We look forward to seeing you there!`,
      };
  }
}

const suviContactInfo: LeadFormContactInfo = {
  name: 'Suvi Rinkineva',
  email: 'suvi.rinkineva@uprightproject.com',
  phone: '+358-50-3595100',
};

const forms: LeadForm[] = [
  {
    id: LeadFormId.BOOK_DEMO_CSRD,
    hubspotId: RequestDemoFormId.GENERIC_CSRD_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.CORPORATE,
  },
  {
    id: LeadFormId.BOOK_DISCOVERY_CALL_CSRD,
    hubspotId: RequestDemoFormId.GENERIC_CSRD_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DISCOVERY_CALL),
    segment: CustomerSegment.CORPORATE,
    contact: suviContactInfo,
  },
  {
    id: LeadFormId.BOOK_DEMO_INVESTOR,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.INVESTOR,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.BOOK_DEMO_INVESTOR_PE,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.INVESTOR_PE,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.BOOK_DEMO_INVESTOR_AM,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.BOOK_DEMO_INVESTOR_AO,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.INVESTOR_AO,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.BOOK_DEMO_CORPORATE,
    hubspotId: RequestDemoFormId.GENERIC_CORPORATE,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.CORPORATE,
  },
  {
    id: LeadFormId.BOOK_DEMO_GENERIC,
    hubspotId: RequestDemoFormId.GENERIC_GA,
    ...getDefaultForCategory(LeadFormCategory.BOOK_DEMO),
    segment: CustomerSegment.GENERAL,
  },
  {
    id: LeadFormId.CONTACT_CSRD,
    hubspotId: RequestDemoFormId.GENERIC_CSRD_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.CORPORATE,
    contact: suviContactInfo,
  },
  {
    id: LeadFormId.CONTACT_GENERIC,
    hubspotId: RequestDemoFormId.GENERIC_GA,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.GENERAL,
  },
  {
    id: LeadFormId.CONTACT_INVESTOR,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.INVESTOR,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.CONTACT_INVESTOR_PE,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.INVESTOR,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.CONTACT_INVESTOR_AM,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.CONTACT_INVESTOR_AO,
    hubspotId: RequestDemoFormId.GENERIC_INVESTOR_BOOK_DEMO_AUG2025,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.INVESTOR_AO,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.CONTACT_CORPORATE,
    hubspotId: RequestDemoFormId.GENERIC_CORPORATE,
    ...getDefaultForCategory(LeadFormCategory.CONTACT),
    segment: CustomerSegment.CORPORATE,
  },
  {
    id: LeadFormId.WEBINAR_DOWNLOAD_CSRD,
    hubspotId: RequestDemoFormId.WEBINAR_DOWNLOAD_CSRD,
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    segment: CustomerSegment.CORPORATE,
  },
  {
    id: LeadFormId.WEBINAR_REGISTER_INVESTOR,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_INVESTOR_AM,
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.WEBINAR_DOWNLOAD_INVESTOR,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_INVESTOR_AM,
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
  {
    id: LeadFormId.DOCUMENTATION_DOWNLOAD_CSRD,
    hubspotId: RequestDemoFormId.DOCUMENTATION_DOWNLOAD_CSRD,
    ...getDefaultForCategory(LeadFormCategory.DOWNLOAD_DOCUMENTATION),
    segment: CustomerSegment.CORPORATE,
    title: 'Download CSRD methodology documentation',
  },
  {
    id: LeadFormId.DOWNLOAD_FUND_IMPACT_REPORT,
    hubspotId: RequestDemoFormId.DOWNLOAD_FUND_IMPACT_REPORT,
    category: LeadFormCategory.DOWNLOAD_DOCUMENTATION,
    title: 'Download the report',
    textBelowTitle: <strong>Fill in your information to access the report!</strong>,
    askConsent: true,
    submitText: 'Download',
    submitEvent: CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT,
    interactionEvent: CustomGtmEvent.UA_DOWNLOAD_FORM_INTERACTED,
    openEvent: CustomGtmEvent.UA_DOWNLOAD_FORM_CLICK,
    hideMessage: true,
    successTitle: 'Success!',
    successMessage: `Thanks for submitting the form. The report has been sent to your email.`,
    segment: CustomerSegment.INVESTOR,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    id: LeadFormId.WEBINAR_REGISTER_CSRD_FOR_INVESTORS,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_CSRD_FOR_INVESTORS,
    textBelowTitle: <b>Access the webinar recording by filling out the form:</b>,
    segment: CustomerSegment.INVESTOR,
    submitText: 'Access the webinar',
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    id: LeadFormId.WEBINAR_REGISTER_FUND_ODAI_LAUNCH,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_FUND_ODAI_LAUNCH,
    textBelowTitle: <b>Access the webinar by filling out the form:</b>,
    segment: CustomerSegment.INVESTOR,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    textBelowTitle: <b>Access the webinar recording by filling out the form:</b>,
    id: LeadFormId.WEBINAR_REGISTER_PE_FUND_LAUNCH,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_PE_FUND_LAUNCH,
    segment: CustomerSegment.INVESTOR_PE,
    submitText: 'Access the webinar',
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    id: LeadFormId.WEBINAR_REGISTER_CSRD_DMA_FINANCIAL_EFFECTS,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_CSRD_DMA_FINANCIAL_EFFECTS,
    segment: CustomerSegment.CORPORATE,
    textBelowTitle: <b>Access the webinar recording by filling out the form::</b>,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_DOWNLOAD),
    id: LeadFormId.WEBINAR_DOWNLOAD_SEPT24_RODENSTOCK,
    hubspotId: RequestDemoFormId.WEBINAR_DOWNLOAD_SEPT24_RODENSTOCK,
    textBelowTitle: <b>Access the webinar by filling out the form:</b>,
    segment: CustomerSegment.CORPORATE,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    id: LeadFormId.WEBINAR_REGISTER_CSRD_DMA_UPDATES,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_CSRD_DMA_UPDATES,
    textBelowTitle: <b>Access the webinar recording by filling out the form:</b>,
    segment: CustomerSegment.CORPORATE,
    submitText: 'Access the webinar',
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    id: LeadFormId.WEBINAR_REGISTER_CSRD_DMA_50K_PROFILES,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_CSRD_DMA_50K_PROFILES,
    textBelowTitle: <b>Access the webinar recording:</b>,
    segment: CustomerSegment.CORPORATE,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Access the webinar recording:</b>,
    id: LeadFormId.WEBINAR_REGISTER_EU_OMNIBUS,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_EU_OMNIBUS,
    segment: CustomerSegment.INVESTOR_PE,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Access the webinar recording:</b>,
    id: LeadFormId.WEBINAR_REGISTER_ESG_IMPACT_DATA_FOR_PRIVATE_MARKETS,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_ESG_IMPACT_DATA_FOR_PRIVATE_MARKETS,
    segment: CustomerSegment.INVESTOR_PE,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Sign up for the webinar on May 6th by filling out the form:</b>,
    id: LeadFormId.WEBINAR_REGISTER_DMA_AS_STRATEGIC_TOOL,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_DMA_AS_STRATEGIC_TOOL,
    segment: CustomerSegment.CORPORATE,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Access the webinar recording:</b>,
    id: LeadFormId.WEBINAR_REGISTER_RETHINKING_SDG,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_RETHINKING_SDG,
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Access the webinar by filling out the form:</b>,
    id: LeadFormId.WEBINAR_REGISTER_FAILING_DMA,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_FAILING_DMA,
    segment: CustomerSegment.INVESTOR,
    showOrganizationTypeField: true,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    id: LeadFormId.WEBINAR_REGISTER_PLG_DMA_LAUNCH,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_PLG_DMA_LAUNCH,
    segment: CustomerSegment.CORPORATE,
    textBelowTitle: <b>Sign up for the webinar on September 9th by filling out the form:</b>,
  },
  {
    ...getDefaultForCategory(LeadFormCategory.WEBINAR_REGISTER),
    textBelowTitle: <b>Sign up for the webinar on August 25th by filling out the form:</b>,
    id: LeadFormId.WEBINAR_REGISTER_STOREBRAND_ASSET_MANAGEMENT,
    hubspotId: RequestDemoFormId.WEBINAR_REGISTER_STOREBRAND_ASSET_MANAGEMENT,
    segment: CustomerSegment.INVESTOR_AM,
    showOrganizationTypeField: true,
  },
];

const leadFormById = keyBy(forms, f => f.id);

export function getLeadForm(id: LeadFormId): LeadForm {
  return leadFormById[id];
}

const LeadFormModalQueryParams = {
  ['lfid']: '' as string | undefined,
} as const;

export type LeadFormModalQueryParams = typeof LeadFormModalQueryParams;

export const leadFormParamKeys = keysOf(LeadFormModalQueryParams);
export const emptyRequestDemoParams = mapValues(keyBy(leadFormParamKeys), () => undefined) as LeadFormModalQueryParams;
